/**
 * Product page and product tile quantity selector component
 */
const quantitySelector = {};

/**
 * Imports
 */
var processInclude = require('base/util');

/**
 * Component configuration constants
 */
const CONFIG = {
    ACTION: {
        INCREASE: 'increase',
        DECREASE: 'decrease'
    },
    CLASS: {
        DISABLED: 'disabled',
        IN_PROGRESS: 'hom-in-progress',
        HIDDEN: 'hom-display--none'
    },
    SELECTORS: {
        ATC_ACTION: '.add-to-cart-url',
        ATC: '.hom-js-add-to-cart',
        COMPONENT: '.hom-js-quantity-selector',
        INPUT_PREFIX: 'input[id^=hom-quantity-selector-value-]',
        INPUT: '#hom-quantity-selector-value-{PRODUCT_UUID}',
        INPUTS: 'input[data-pid={{PID}}]',
        LINE_ITEM: '.product-line-item',
        MINUS: '.hom-js-quantity-selector-minus',
        PLUS: '.hom-js-quantity-selector-plus'
    }
};

/**
 * Private functions
 */

/**
 * Verify if all arguments have a valid value.
 * @param {any} arguments - 1 or more arguments to validate
 * @returns {boolean} true if all arguments are valid
 */
function isValid() {
    var argument;

    for (var i = 0; i < arguments.length; i++) {
        argument = arguments[i];

        if (argument === undefined && argument === null && argument === 'undefined' && argument === 'null') {
            return false;
        }
    }

    return true;
}

/**
 * Verify if the action is valid, taking into account the values expected in configuration.
 * @param {string} action - action to validate
 * @returns {boolean} true if is an expected action value
 */
function isValidAction(action) {
    return action === CONFIG.ACTION.INCREASE || action === CONFIG.ACTION.DECREASE;
}

/**
 * Verify if the provided element is contained inside a product line item component.
 * @param {JQuery<HTMLElement>} $element - quantity selector JQuery element
 * @returns {boolean} true if the element is contained inside a product line item component
 */
function isLineItem($element) {
    if (!isValid($element)) {
        return false;
    }

    return $element.closest(CONFIG.SELECTORS.LINE_ITEM).length;
}

/**
 * Remove the quantity selector closest line item HTML element.
 * @param {JQuery<HTMLElement>} $element - quantity selector JQuery element
 */
function removeLineItem($element) {
    if (!isValid($element)) {
        return;
    }

    const lineItem = $element.closest(CONFIG.SELECTORS.LINE_ITEM);

    if (lineItem.length) {
        lineItem.remove();
    }
}

/**
 * Collection of functions which set the status of an element.
 */
const setStatus = {
    /**
     * Add the disabled class to the given element.
     * @param {JQuery<HTMLElement>} $element - element to add the disabled class
     */
    disabled: function ($element) {
        if (!isValid($element)) {
            return;
        }

        $element.addClass(CONFIG.CLASS.DISABLED);
    },

    /**
     * Remove the disabled class of the given element.
     * @param {JQuery<HTMLElement>} $element - element to remove the disabled class
     */
    enabled: function ($element) {
        if (!isValid($element)) {
            return;
        }

        $element.removeClass(CONFIG.CLASS.DISABLED);
    },

    /**
     * Add the in progress class of the given element.
     * @param {JQuery<HTMLElement>} $element - element to add the in progress class
     */
    inProgress: function ($element) {
        if (!isValid($element)) {
            return;
        }

        $element.addClass(CONFIG.CLASS.IN_PROGRESS);
    },

    /**
     * Remove the in progress class of the given element.
     * @param {JQuery<HTMLElement>} $element - element to remove the in progress class
     */
    done: function ($element) {
        if (!isValid($element)) {
            return;
        }

        $element.removeClass(CONFIG.CLASS.IN_PROGRESS);
    },

    /**
     * Add the hidden class to the given element.
     * @param {JQuery<HTMLElement>} $element - element to add the hidden class
     */
    hidden: function ($element) {
        if (!isValid($element)) {
            return;
        }

        $element.addClass(CONFIG.CLASS.HIDDEN);
    },

    /**
     * Remove the hidden class to the given element.
     * @param {JQuery<HTMLElement>} $element - element to remove the hidden class
     */
    shown: function ($element) {
        if (!isValid($element)) {
            return;
        }

        $element.removeClass(CONFIG.CLASS.HIDDEN);
    }
};

/**
 * Collection of functions which represent server interactions.
 */
const server = {
    /**
     * Call the backend Cart-UpdateQuantity controller.
     * @param {string} action - endpoint URL
     * @param {string} uuid - product uuid
     * @param {string} pid - product id
     * @param {number} value - quantity to set
     * @param {number} max - max allowed quantity
     * @param {number} step - step quantity
     * @param {JQuery<HTMLElement>} $input - input which includes the quantity selector
     * @param {JQuery<HTMLElement>} $plus - quantity selector plus element
     * @param {JQuery<HTMLElement>} $trigger - element which triggers the quantity selector event
     */
    updateQuantity: function (action, uuid, pid, value, max, step, $input, $plus, $trigger) {
        if (!isValid(action, uuid, pid, value, max, step, $input, $plus, $trigger)) {
            return;
        }

        // 1. Start loading spinner
        $.spinner().start();

        // 2. Change status to in progress
        setStatus.inProgress($trigger);

        $.ajax({
            url: action,
            method: 'POST',
            data: {
                uuid: uuid,
                pid: pid,
                quantity: value,
                isIncrease: step > 0
            },
            success: function (response) {
                // 1. Update the quantity of all line items that are in the page and share the same ID
                $('body').trigger('hom:quantitySelector:updateAllQuantities', {
                    pid: pid,
                    value: value,
                    max: max,
                    $plus: $plus
                });

                // 2. Trigger the listener to update the subtotal and to to update the item quantity in the cart icon
                if (response.basket) {
                    $('body').trigger('hom:subTotal:update', response.basket);
                    $('body').trigger('count:update', response.basket);
                }
            },
            complete: function (response) {
                // 1. Set done status
                setStatus.done($trigger);

                // 2. Stop loading spinner
                $.spinner().stop();

                // 3. Throw an alert message
                $.alert().success(response.responseJSON.message);
            }
        });
    },

    /**
     * Call the backend Cart-RemoveProduct controller.
     * @param {string} action - endpoint URL
     * @param {string} pid - product id
     * @param {JQuery<HTMLElement>} $trigger - element which triggers the quantity selector event
     */
    // eslint-disable-next-line no-unused-vars
    remove: function (action, pid, $trigger) {
        if (!isValid(action, pid, $trigger)) {
            return;
        }

        // 1. Start loading spinner
        $.spinner().start();

        // 2. Change status to in progress
        setStatus.inProgress($trigger);

        $.ajax({
            url: action,
            method: 'POST',
            data: {
                pid: pid
            },
            success: function (response) {
                // 1. Remove all line items that are in the page and share the same ID
                $('body').trigger('hom:quantitySelector:removeAll', {
                    pid: pid
                });

                // 2. Render empty minicart HTML if basket is empty
                if (response.isEmpty && response.emptyHTML) {
                    $('body').trigger('hom:minicart:empty', response);
                }

                // 3. Trigger the listener to update the subtotal and to to update the item quantity in the cart icon
                if (response.basket) {
                    $('body').trigger('hom:subTotal:update', response.basket);
                    $('body').trigger('count:update', response.basket);
                }
            },
            complete: function (response) {
                // 1. Set done status
                setStatus.done($trigger);

                // 2. Stop loading spinner
                $.spinner().stop();

                // 3. Throw an alert message
                $.alert().error(response.responseJSON.message);
            }
        });
    }
};

/**
 * Collection of getter functions related with all quantity selector values.
 */
const getValue = {
    /**
     * Get input element min value.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the min value
     * @returns {number | undefined} min quantity
     */
    min: function ($input) {
        const value = $input.attr('min');

        if (!isValid(value)) {
            return undefined;
        }

        return parseInt(value, 10);
    },

    /**
     * Get input element max value.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the max value
     * @returns {number | undefined} max quantity
     */
    max: function ($input) {
        const value = $input.attr('max');

        if (!isValid(value)) {
            return undefined;
        }

        return parseInt(value, 10);
    },

    /**
     * Get element step value taking into account the data value attribute.
     * @param {JQuery<HTMLElement>} $element - minus or plus jquery element
     * @param {string} action - quantity selector action (increase or decrease)
     * @returns {number | undefined} step quantity
     */
    step: function ($element, action) {
        const value = $element.data('value');

        if (!isValid(value) && !isValidAction(action)) {
            return undefined;
        }

        return action === CONFIG.ACTION.INCREASE ? Math.abs(value) : -Math.abs(value);
    },

    /**
     * Get input current value taking into account the value attribute.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the current value
     * @returns {number | undefined} current value
     */
    current: function ($input) {
        const value = $input.val();

        if (!isValid(value)) {
            return undefined;
        }

        return parseInt(value, 10);
    },

    /**
     * Get product id.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the product id
     * @returns {number | undefined} product id
     */
    pid: function ($input) {
        const value = $input.data('pid');

        if (!isValid(value)) {
            return undefined;
        }

        return value;
    },

    /**
     * Get product line item uuid.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the product line item uuid
     * @returns {string | undefined} product line item uuid
     */
    uuid: function ($input) {
        const value = $input.data('pli-uuid');

        if (!isValid(value)) {
            return undefined;
        }

        return value;
    },

    /**
     * Get update product line item quantity url action.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the update product line item quantity url action
     * @returns {string | undefined} product line item quantity url action
     */
    action: function ($input) {
        const value = $input.data('action');

        if (!isValid(value)) {
            return undefined;
        }

        return value;
    },

    /**
     * Get remove product line item url action.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the remove product line item url action
     * @returns {string | undefined} remove product line item url action
     */
    actionRemove: function ($input) {
        const value = $input.data('action-remove');

        if (!isValid(value)) {
            return undefined;
        }

        return value;
    }
};

/**
 * Collection of setter functions related with all quantity selector values.
 */
const setValue = {
    /**
     * Set input current value taking into account the value to increase or decrease.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the current value
     * @param {JQuery<HTMLElement>} $trigger - jquery element which trigger the update quantity event
     * @param {number} step - value to increase or decrease to the current value
     * @returns {number | undefined} new current value
     */
    current: function ($input, $trigger, step) {
        var newValue = 0;

        // Constants
        const action = getValue.action($input);
        const actionRemove = getValue.actionRemove($input);
        const pid = getValue.pid($input);
        const min = getValue.min($input);
        const max = getValue.max($input);
        const uuid = getValue.uuid($input);
        const value = getValue.current($input);
        const $plus = $input.siblings(CONFIG.SELECTORS.PLUS);

        if (!isValid(action, min, max, value, pid, $trigger)) {
            return undefined;
        }

        // 1. Calculate the new value
        newValue = parseInt(value, 10) + step;

        // 2. Two possible scenarios:
        //    - is value valid ? Set input with the new value
        //    - is value less than min ? Remove the quantity selector
        if (newValue >= min && newValue <= max) {
            // 2.1 Execute AJAX call
            server.updateQuantity(action, uuid, pid, newValue, max, step, $input, $plus, $trigger);
        } else if (newValue < value && newValue === min + step) {
            server.remove(actionRemove, pid, $trigger);
        }

        return newValue;
    },

    /**
     * Set input current value.
     * @param {JQuery<HTMLInputElement>} $input - input jquery element which contains the current value
     * @param {number} value - input value
     * @returns {undefined | number} input value or undefined if not valid
     */
    input: function ($input, value) {
        if (!isValid($input, value)) {
            return undefined;
        }

        $input.val(value);

        return value;
    }
};

/**
 * Collection of functions which attach event listeners to all interactive HTMLElement type objects.
 */
const onClick = {
    /**
     * Attach event listener associated with the quantity selector minus icon.
     */
    minus: function () {
        $('body').on('click', CONFIG.SELECTORS.MINUS, function (e) {
            e.preventDefault();

            // Constants
            const $this = $(this);
            const $input = $this.siblings(CONFIG.SELECTORS.INPUT_PREFIX);
            const step = getValue.step($this, CONFIG.ACTION.DECREASE);

            // 1. Is already updating quantity?
            if ($this.hasClass(CONFIG.CLASS.IN_PROGRESS)) {
                return;
            }

            // 2. Set value
            setValue.current($input, $this, step);
        });
    },

    /**
     * Attach event listener associated with the quantity selector plus icon.
     */
    plus: function () {
        $('body').on('click', CONFIG.SELECTORS.PLUS, function (e) {
            e.preventDefault();

            // Constants
            const $this = $(this);
            const $input = $this.siblings(CONFIG.SELECTORS.INPUT_PREFIX);
            const step = getValue.step($this, CONFIG.ACTION.INCREASE);

            // 1. Is already updating quantity?
            if ($this.hasClass(CONFIG.CLASS.IN_PROGRESS)) {
                return;
            }

            // 2. Set value
            setValue.current($input, $this, step);
        });
    }
};

/**
 * Collection of functions which attach custom event listeners.
 */
const onCustom = {
    /**
     * Attach hide quantity selector custom event listener.
     */
    hide: function () {
        $('body').on('hom:quantitySelector:hide', function (e, data) {
            if (isValid(data) && '$element' in data && data.$element.length) {
                setStatus.hidden(data.$element);
            }
        });
    },

    /**
     * Attach show quantity selector custom event listener.
     */
    show: function () {
        $('body').on('hom:quantitySelector:show', function (e, data) {
            if (isValid(data) && '$element' in data && data.$element.length) {
                setStatus.shown(data.$element);
            }
        });
    },

    /**
     * Attach quantity selector set quantity custom event listener.
     */
    setQuantity: function () {
        $('body').on('hom:quantitySelector:setQuantity', function (e, data) {
            if (
                isValid(data) &&
                '$element' in data &&
                data.$element.length &&
                'value' in data &&
                typeof data.value === 'number'
            ) {
                setValue.input(data.$element.find(CONFIG.SELECTORS.INPUT_PREFIX), data.value);
            }
        });
    },

    /**
     * Attach the change quantity selectorplus icon custom event listener.
     */
    setPlusStatus: function () {
        $('body').on('hom:quantitySelector:setPlusStatus', function (e, data) {
            if (isValid(data) && '$element' in data && data.$element.length) {
                const $element = data.$element;
                const $input = $element.find(CONFIG.SELECTORS.INPUT_PREFIX);
                const $plus = $element.find(CONFIG.SELECTORS.PLUS);
                const max = getValue.max($input);
                const value = getValue.current($input);

                if (!isValid($input, $plus, max, value)) {
                    return;
                }

                // 1. Set "+" element status
                if (value === max) {
                    setStatus.disabled($plus);
                } else {
                    setStatus.enabled($plus);
                }
            }
        });
    },

    /**
     * Attach a remove all products that have the same ID.
     */
    removeAll: function () {
        $('body').on('hom:quantitySelector:removeAll', function (e, data) {
            if (isValid(data) && 'pid' in data && data.pid) {
                const $elements = $(CONFIG.SELECTORS.INPUTS.replace('{{PID}}', data.pid));

                for (let i = 0; i < $elements.length; i++) {
                    var $element = $($elements[i]);
                    var $closestQuantitySelector = $element.closest(CONFIG.SELECTORS.COMPONENT);
                    var $siblingAddToCart = $closestQuantitySelector.siblings(CONFIG.SELECTORS.ATC);

                    //  2 possible scenarios:
                    //  - Hide quantity selector & show ATC button
                    //  - If is minicart remove the product line item
                    if (!isLineItem($closestQuantitySelector)) {
                        $('body').trigger('hom:quantitySelector:hide', { $element: $closestQuantitySelector });
                        $('body').trigger('hom:addToCart:show', { $element: $siblingAddToCart });
                    } else {
                        removeLineItem($closestQuantitySelector);
                    }
                }
            }
        });
    },

    /**
     * Attach a remove all products that have the same ID.
     */
    updateAllQuantities: function () {
        $('body').on('hom:quantitySelector:updateAllQuantities', function (e, data) {
            if (isValid(data) && 'pid' in data && 'value' in data && 'max' in data && '$plus' in data) {
                const $elements = $(CONFIG.SELECTORS.INPUTS.replace('{{PID}}', data.pid));

                for (let i = 0; i < $elements.length; i++) {
                    var $element = $($elements[i]);
                    var $plus = $(data.$plus);

                    $element.val(data.value);

                    // 1. Set "+" element status
                    if (data.value === data.max) {
                        setStatus.disabled($plus);
                    } else {
                        setStatus.enabled($plus);
                    }
                }
            }
        });
    }
};

/**
 * Exposed event listener functions
 */
quantitySelector.setEvents = function () {
    processInclude(onClick);
    processInclude(onCustom);
};

module.exports = quantitySelector;
